import type { MinimarkNode } from '@nuxt/content'

interface ElementProps extends Record<string, unknown> {
  href?: string
  src?: string
  link?: string
  target?: string
  id?: string
}

interface DestructuredElement {
  tag: string
  props: ElementProps
  children: MinimarkNode[]
}

function isElement(node: MinimarkNode): node is [string, Record<string, unknown>, ...MinimarkNode[]] {
  return Array.isArray(node) && node.length >= 2 && typeof node[0] === 'string'
}

function destructureElement(node: MinimarkNode): DestructuredElement | null {
  if (!isElement(node)) {
    return null
  }
  const [tag, props, ...children] = node
  return {
    tag,
    props: props as ElementProps,
    children,
  }
}

export function optimizeContentTags(node: MinimarkNode, path: string): MinimarkNode {
  if (typeof node === 'string') {
    return node
  }

  const element = destructureElement(node)
  if (!element) {
    return node
  }

  const { tag, props, children } = element

  if (tag === 'a') {
    // fix anchor links that start with #
    if (props.href?.startsWith('#')) {
      props.href = `${path}${props.href}`
    }
    // fix anchor links that contain the current path
    else if (props.href) {
      const href = props.href.split('#')[0]
      if (href && path.includes(href)) {
        props.href = `${path}#${props.href.split('#')[1]}`
      }
    }

    // add target="_blank"
    if (props.target !== '_blank') {
      props.target = '_blank'
    }
  }
  else if (tag === 'img' && props.src?.startsWith('./')) {
    props.src = props.src.slice(1)
  }

  // walk children
  const updatedChildren = children.map(child => optimizeContentTags(child, path))
  return [tag, props, ...updatedChildren] as MinimarkNode
}

interface ValidationResult {
  type: 'image' | 'link'
  path: string
  source: string
}

async function validateURL(url: string): Promise<{ ok: boolean, status?: number, statusText?: string }> {
  try {
    const response = await fetch(url)
    return {
      ok: response.ok,
      status: response.status,
      statusText: response.statusText,
    }
  }
  catch {
    return { ok: false }
  }
}

export async function validateLinksAndImages(node: MinimarkNode, path: string): Promise<ValidationResult[]> {
  if (typeof node === 'string') {
    return []
  }

  const element = destructureElement(node)
  if (!element) {
    return []
  }

  const results: ValidationResult[] = []
  const { tag, props, children } = element

  // Validate images
  if (tag === 'img' && props.src?.startsWith('http')) {
    const validation = await validateURL(props.src)
    if (!validation.ok) {
      results.push({
        type: 'image',
        path,
        source: props.src,
      })
    }
  }

  // Validate links
  if (tag === 'a' || tag === 'resource') {
    const link = props.href || props.link
    if (link?.startsWith('http')) {
      const validation = await validateURL(link)
      if (!validation.ok) {
        results.push({
          type: 'link',
          path,
          source: link,
        })
      }
    }
  }

  let childResults: ValidationResult[] = []
  if (children.length > 0) {
    childResults = (await Promise.all(children.map(child => validateLinksAndImages(child, path)))).flat()
  }

  return [...results, ...childResults]
}

export function extractMarkdownSection(nodes: MinimarkNode[], section: string): MinimarkNode[] | null {
  if (!Array.isArray(nodes)) {
    return null
  }
  let start: number | undefined
  let end: number | undefined
  let sectionTag: string | undefined

  nodes.forEach((node, index) => {
    const element = destructureElement(node)
    if (!element) {
      return
    }

    const { tag, props } = element
    if (props.id === section) {
      start = index
      sectionTag = tag
    }
    else if (start && tag === sectionTag) {
      end = index
    }
  })

  return start === undefined ? null : nodes.slice(start, end)
}
